import React, { useContext, useEffect, useState } from 'react'
import './CSS/ShopCategory.css'
import { ShopContext } from '../Context/ShopContext'
import Item from '../Components/Item/Item'
import { useParams } from 'react-router-dom'
import API_BASE_URL from '../config'
import { getOptimizedImageUrl, setOptimizedImageFallback } from '../utils/imageOptimization'

const ShopCategory = (props) => {
  const { all_product } = useContext(ShopContext)
  const { category } = useParams() // Get category from URL params
  
  // State for category data and products
  const [categoryData, setCategoryData] = useState(null)
  const [filteredProducts, setFilteredProducts] = useState([])
  const [sortOption, setSortOption] = useState('default')
  const [showCount, setShowCount] = useState(12) // Number of products to show initially

  // Determine which category to use
  const currentCategory = category || props.category

  // Fetch category data from backend
  useEffect(() => {
    const fetchCategoryData = async () => {
      try {
        // Skip for legacy categories that don't exist in backend
        if (['men', 'women', 'kid'].includes(currentCategory)) {
          setCategoryData({
            name: currentCategory === 'kid' ? 'Kids' : 
                  currentCategory === 'men' ? 'Mens' : 
                  'Womens',
            description: '',
            image: props.banner || ''
          })
          return
        }

        const response = await fetch(`${API_BASE_URL}/api/categories/${currentCategory}`)
        const data = await response.json()
        
        if (data.success) {
          setCategoryData(data.category)
        } else {
          // If category not found in backend, use props
          setCategoryData({
            name: currentCategory.charAt(0).toUpperCase() + currentCategory.slice(1),
            description: '',
            image: props.banner || ''
          })
        }
      } catch (error) {
        // Fallback to props
        setCategoryData({
          name: props.categoryName || currentCategory.charAt(0).toUpperCase() + currentCategory.slice(1),
          description: '',
          image: props.banner || ''
        })
      }
    }

    if (currentCategory) {
      fetchCategoryData()
    }
  }, [currentCategory, props.banner, props.categoryName])

  // Filter and sort products based on category
  useEffect(() => {
    if (!all_product || !currentCategory) return

    let products = []
    
    if (currentCategory === 'all') {
      // Show all products
      products = [...all_product]
    } else {
      // Filter products by category
      products = all_product.filter(item => {
        // Check multiple possible category fields
        const itemCategory = item.category?.toLowerCase()
        const itemCategorySlug = item.category_slug?.toLowerCase()
        const itemMainCategory = item.main_category?.toLowerCase()
        
        if (currentCategory === 'mens') {
          return itemCategory === 'men' || itemCategory === 'mens' || itemCategory === 'shoes'
        } else if (currentCategory === 'womens') {
          return itemCategory === 'woman' || itemCategory === 'womens' || itemCategory === 'shoes'
        } else if (currentCategory === 'kids') {
          return itemCategory === 'kid' || itemCategory === 'kids'
        } else if (currentCategory === 'shoes') {
          return itemCategory === 'shoes'
        } else {
          // For dynamic categories from admin
          return (
            itemCategory === currentCategory ||
            itemCategorySlug === currentCategory ||
            itemMainCategory === currentCategory ||
            (item.categories && Array.isArray(item.categories) && 
             item.categories.includes(currentCategory))
          )
        }
      })
    }

    // Apply sorting
    const sortedProducts = sortProducts(products, sortOption)
    setFilteredProducts(sortedProducts)
  }, [all_product, currentCategory, sortOption])

  // Sort products based on selected option
  const sortProducts = (products, option) => {
    const sorted = [...products]
    
    switch (option) {
      case 'price-low':
        return sorted.sort((a, b) => a.new_price - b.new_price)
      case 'price-high':
        return sorted.sort((a, b) => b.new_price - a.new_price)
      case 'name-asc':
        return sorted.sort((a, b) => a.name.localeCompare(b.name))
      case 'name-desc':
        return sorted.sort((a, b) => b.name.localeCompare(a.name))
      case 'newest':
        return sorted.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
      default:
        return sorted // Default order
    }
  }

  const handleSortChange = (e) => {
    setSortOption(e.target.value)
  }

  const handleLoadMore = () => {
    setShowCount(prev => Math.min(prev + 12, filteredProducts.length))
  }

  // Get banner image
  const getBannerImage = () => {
    if (categoryData?.image) {
      return categoryData.image
    } else if (props.banner) {
      return props.banner
    } else {
      // Return a default banner based on category
      switch (currentCategory) {
        case 'mens':
          return '/images/mens-banner.jpg'
        case 'womens':
          return '/images/womens-banner.jpg'
        case 'kids':
          return '/images/kids-banner.jpg'
        default:
          return '/images/default-banner.jpg'
      }
    }
  }

  // Calculate showing range

  const showingStart = "Showing     " + 1
  const showingEnd = Math.min(showCount, filteredProducts.length)
  const canLoadMore = showCount < filteredProducts.length

  // Determine if we should show banner
  const shouldShowBanner = () => {
    if (!currentCategory || currentCategory === 'all') {
      return true
    }
    
    return false
  }

  return (
    <div className='shop-category'>
      {/* Show banner ONLY on homepage */}
      {shouldShowBanner() && (
        <img 
          className='shopcategory-banner' 
          src={getOptimizedImageUrl(getBannerImage(), { width: 1400, quality: 74 })} 
          alt={currentCategory} 
          loading="eager"
          decoding="async"
          fetchpriority="high"
          onError={(e) => {
            setOptimizedImageFallback(e, getBannerImage(), '/images/default-banner.jpg')
          }}
        />
      )}
      
      {/* Category description for homepage */}
      {shouldShowBanner() && categoryData?.description && (
        <div className="category-description">
          <p>{categoryData.description}</p>
        </div>
      )}

      <div className="shopcategory-indexSort">
        {/* Only show "1 of 4" format */}
        <p>
          <span>{showingEnd > 0 ? `${showingStart} of ${filteredProducts.length} products` : 'No products found'}</span>
        </p>
        <div className="shopcategory-sort">
          <select value={sortOption} onChange={handleSortChange}>
            <option value="default">Sort by: Default</option>
            <option value="price-low">Price: Low to High</option>
            <option value="price-high">Price: High to Low</option>
            <option value="name-asc">Name: A to Z</option>
            <option value="name-desc">Name: Z to A</option>
            <option value="newest">Newest First</option>
          </select>
        </div>
      </div>

      <div className="shopcategory-products">
        {filteredProducts.length > 0 ? (
          filteredProducts.slice(0, showCount).map((item, i) => (
            <Item  
              key={item.id || i} 
              id={item.id} 
              name={item.name} 
              image={item.main_image || item.image} 
              new_price={item.new_price} 
              old_price={item.old_price}
            />
          ))
        ) : (
          <div className="no-products-found">
            <p>No products found in this category.</p>
            <a href="/" className="back-to-shop">Browse All Products</a>
          </div>
        )}
      </div>

      {canLoadMore && (
        <div className="shopcategory-loadmore" onClick={handleLoadMore}>
          Load More Products ({filteredProducts.length - showCount} remaining)
        </div>
      )}
    </div>
  )
}

export default ShopCategory

